import Vue from 'vue';
import OmnicaPopper, { addCloserListeners, removeCloserListeners } from '@omnica/popper';
import isEqual from 'lodash.isequal';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

var script = Vue.extend({
    model: {
        prop: 'shown',
        event: 'update:shown',
    },
    props: {
        /** Функция, возвращающая элемент цели, к которой будет привязан плавающий элемент */
        target: {
            type: Function,
            required: true,
        },
        /** События, по которым производится переключение видимости */
        triggers: {
            type: Array,
            default: function () { return ['hover']; },
        },
        /** События внутри плавающего элемента, по которым производится переключение видимости */
        popperTriggers: {
            type: Array,
            default: function () { return []; },
        },
        /** Флаг для ручного переключения видимости */
        shown: {
            type: Boolean,
            default: false,
        },
        /** Сторона цели, у которой появится плавающий элемент */
        placement: {
            type: String,
            default: 'bottom',
        },
        /** Способ позиционирования, значение CSS свойство position */
        strategy: {
            type: String,
            default: 'absolute',
        },
        /**
         * Контейнер, либо селектор контейнера, внутрь которого будет помещена верстка плавающего элемента.
         * Если не указан, будет взят из глобальных настроек или document.body
         */
        container: {
            type: null,
            validator: function (value) { return value === null || value instanceof Element || typeof value === 'string'; },
            default: null,
        },
        /** Элемент, массив элементов, относительно которых будет рассчитываться перекрытие */
        boundary: {
            type: [String, Element, Array],
            default: undefined,
        },
        /** Сдвиг по основной оси, определяемой из положения элемента */
        offsetMainAxis: {
            type: [Number, String],
            validator: function (value) { return !isNaN(Number(value)); },
            default: 8,
        },
        /** Сдвиг по перекрестной оси, определяемой из положения элемента */
        offsetCrossAxis: {
            type: [Number, String],
            validator: function (value) { return !isNaN(Number(value)); },
            default: 0,
        },
        /** Стратегии позиционирования при обнаружении перекрытия */
        overflow: {
            type: Array,
            default: function () { return ['flip', 'shift']; },
        },
        /** Задержка перед показом/скрытием плавающего элемента */
        delay: {
            type: [Number, String, Object],
            validator: function (value) { return typeof value === 'object' || !isNaN(Number(value)); },
            default: 0,
        },
        /** Флаг, отключающий возможность показа плавающего элемента */
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Время, через которое плавающий элемент удаляется со страницы, если был скрыт */
        disposeTimeout: {
            type: null,
            default: 5000,
        },
        /** Флаг автоматического сокрытия при событии триггера за пределами элемента */
        autoHide: {
            type: Boolean,
            default: false,
        },
        /** Темная тема */
        dark: {
            type: Boolean,
            default: false,
        },
        /** Добавляет стрелку-указатель */
        withArrow: {
            type: Boolean,
            default: false,
        },
    },
    data: function () { return ({
        isShown: false,
    }); },
    watch: {
        shown: function (shown) {
            shown ? this.show() : this.hide();
        },
        triggers: function (triggers) {
            var _a;
            (_a = this._omnicaPopper) === null || _a === void 0 ? void 0 : _a.changeTargetTriggers(triggers);
        },
        popperTriggers: function (triggers) {
            var _a;
            (_a = this._omnicaPopper) === null || _a === void 0 ? void 0 : _a.changePopperTriggers(triggers);
        },
        container: function (newContainer, oldContainer) {
            var _a;
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_b) {
                    if (newContainer !== oldContainer) {
                        (_a = this._omnicaPopper) === null || _a === void 0 ? void 0 : _a.changeContainer(newContainer);
                    }
                    return [2 /*return*/];
                });
            });
        },
        autoHide: function (autoHide) {
            var popper = this._omnicaPopper;
            if (popper) {
                popper.autoHide = autoHide;
            }
        },
        delay: function (delay) {
            var popper = this._omnicaPopper;
            if (popper) {
                popper.delay = delay;
            }
        },
        disabled: function (disabled) {
            var popper = this._omnicaPopper;
            if (popper) {
                disabled ? popper.dispose() : popper.activate(this.shown);
            }
        },
        disposeTimeout: function () {
            var popper = this._omnicaPopper;
            if (popper) {
                popper.disposeTimeout = this.disposeTimeout;
            }
        },
    },
    created: function () {
        var _this = this;
        this.$watch(function () { return _this.target(); }, function (newTarget, oldTarget) {
            var _a;
            if (newTarget !== oldTarget) {
                (_a = _this._omnicaPopper) === null || _a === void 0 ? void 0 : _a.changeTarget(_this.target);
            }
        });
        this.$watch(function () { return ({
            boundary: _this.boundary,
            offsetMainAxis: _this.offsetMainAxis,
            offsetCrossAxis: _this.offsetCrossAxis,
            placement: _this.placement,
            strategy: _this.strategy,
            overflow: _this.overflow,
        }); }, function (newProps, oldProps) {
            if (newProps.boundary !== oldProps.boundary ||
                Number(newProps.offsetMainAxis) !== Number(oldProps.offsetMainAxis) ||
                Number(newProps.offsetCrossAxis) !== Number(oldProps.offsetCrossAxis) ||
                newProps.placement !== oldProps.placement ||
                newProps.strategy !== oldProps.strategy ||
                newProps.overflow.some(function (behavior) { return !oldProps.overflow.includes(behavior); }) ||
                oldProps.overflow.some(function (behavior) { return !newProps.overflow.includes(behavior); })) {
                var popper = _this._omnicaPopper;
                if (popper) {
                    popper.options = {
                        placement: newProps.placement,
                        offsetMainAxis: newProps.offsetMainAxis,
                        offsetCrossAxis: newProps.offsetCrossAxis,
                        overflow: newProps.overflow,
                        strategy: newProps.strategy,
                        boundary: newProps.boundary,
                    };
                    popper.adjust();
                }
            }
        });
    },
    mounted: function () {
        var _this = this;
        var context = this;
        var popper = new OmnicaPopper(this.$el, this.target, {
            targetTriggers: this.triggers,
            popperTriggers: this.popperTriggers,
            delay: this.delay,
            autoHide: this.autoHide,
            disposeTimeout: this.disposeTimeout,
            placement: this.placement,
            offsetMainAxis: Number(this.offsetMainAxis),
            offsetCrossAxis: Number(this.offsetCrossAxis),
            overflow: this.overflow,
            strategy: this.strategy,
            boundary: this.boundary,
            container: this.container,
        });
        popper.on('update:shown', function (shown) {
            _this.isShown = shown;
            /** Смена видимости плавающего элемента */
            _this.$emit('update:shown', shown);
        });
        popper.on('show', function () {
            /** Появление плавающего элемента */
            _this.$emit('show');
        });
        popper.on('shown', function () {
            /** Смена видимости плавающего элемента на отображение на форме */
            _this.$emit('apply:show');
        });
        popper.on('hide', function () {
            /** Сокрытие плавающего элемента */
            _this.$emit('hide');
        });
        popper.on('hide:by-closer', function () {
            /** Сокрытие плавающего элемента по клику на элемент с директивой vPopperClose */
            _this.$emit('hide:directive');
        });
        popper.on('hide:by-miss-click', function () {
            /** Сокрытие плавающего элемента по клику вне области контейнера */
            _this.$emit('hide:auto');
        });
        popper.on('hidden', function () {
            /** Смена видимости плавающего элемента на сокрытие */
            _this.$emit('apply:hide');
        });
        popper.on('dispose', function () {
            /** Открепление от страницы плавающего элемента */
            _this.$emit('dispose');
        });
        popper.initialize(this.disabled);
        if (this.shown && !this.disabled) {
            popper.show();
        }
        context._omnicaPopper = popper;
        this.$once('hook:beforeDestroy', function () {
            var _a;
            (_a = context._omnicaPopper) === null || _a === void 0 ? void 0 : _a.destroy();
            context._omnicaPopper = undefined;
        });
    },
    activated: function () {
        this.shown ? this.show() : this.hide();
    },
    deactivated: function () {
        this.hide();
    },
    methods: {
        show: function (immediately) {
            var _a;
            if (immediately === void 0) { immediately = false; }
            if (!this.disabled) {
                (_a = this._omnicaPopper) === null || _a === void 0 ? void 0 : _a.show(immediately);
            }
        },
        hide: function (immediately) {
            var _a;
            if (immediately === void 0) { immediately = false; }
            (_a = this._omnicaPopper) === null || _a === void 0 ? void 0 : _a.hide(immediately);
        },
    },
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    _vm._g(
      _vm._b(
        {
          class: {
            "omnica-popper": true,
            "omnica-popper_shown": _vm.isShown,
            "omnica-popper_hidden": !_vm.isShown,
            "omnica-popper_dark": _vm.dark,
          },
          on: {
            transitionend: function ($event) {
              _vm.isShown ? _vm.$emit("shown") : _vm.$emit("hidden");
            },
          },
        },
        "div",
        _vm.$attrs,
        false
      ),
      _vm.$listeners
    ),
    [
      _vm.withArrow
        ? _c("div", {
            staticClass: "omnica-popper__arrow",
            attrs: { "data-arrow": "" },
          })
        : _vm._e(),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "omnica-popper__content" },
        [_vm._t("default")],
        2
      ),
    ]
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var arraify = function (value) { return Array.isArray(value)
    ? __spreadArray([], value, true)
    : [value]; };
var render = function (content, h) { return arraify(typeof content === 'function'
    ? content(h)
    : content); };

var registry = new Map;
function createPopper(el, _a) {
    var content = _a.content, options = __rest(_a, ["content"]);
    var target = function () { return el; };
    var definition = Vue.observable({ content: content, options: options });
    var app = new Vue({
        render: function (h) {
            var _a;
            var _b = definition.options, attrs = _b.attrs, classes = _b.classes, options = __rest(_b, ["attrs", "classes"]);
            return h(__vue_component__, {
                attrs: attrs,
                class: classes,
                props: __assign(__assign({}, options), { target: target }),
                on: {
                    'update:shown': function (shown) { return el.classList.toggle('omnica-popper-target_active', shown); },
                },
            }, render((_a = definition.content) !== null && _a !== void 0 ? _a : '', this.$createElement));
        },
    });
    app === null || app === void 0 ? void 0 : app.$mount(document.body.appendChild(document.createElement('div')));
    el.classList.toggle('omnica-popper-target', true);
    el.classList.toggle('omnica-popper-target_active', options.shown);
    var popper = {
        update: function (_a) {
            var content = _a.content, options = __rest(_a, ["content"]);
            definition.content = content;
            if (!isEqual(definition.options, options)) {
                definition.options = options;
            }
        },
        release: function () {
            app === null || app === void 0 ? void 0 : app.$destroy();
            app = null;
        },
    };
    registry.set(el, popper);
    return popper;
}
function destroyPopper(el) {
    var popper = registry.get(el);
    if (popper) {
        registry.delete(el);
        popper.release();
    }
    if (el.classList) {
        el.classList.toggle('omnica-popper-target', false);
        el.classList.toggle('omnica-popper-target_active', false);
    }
}
var bind = function (el, binding, node) {
    var _a, _b, _c, _d, _e;
    var options = __assign({ content: null, shown: false, disabled: false, container: (_e = (_d = (_c = ((_a = node === null || node === void 0 ? void 0 : node.componentInstance) !== null && _a !== void 0 ? _a : (_b = node === null || node === void 0 ? void 0 : node.parent) === null || _b === void 0 ? void 0 : _b.componentInstance)) === null || _c === void 0 ? void 0 : _c.$omnicaPopper) === null || _d === void 0 ? void 0 : _d.container) !== null && _e !== void 0 ? _e : null, dark: false, attrs: {} }, binding.value);
    if (!options.content || options.disabled) {
        destroyPopper(el);
    }
    else {
        var popper = registry.get(el);
        if (popper) {
            popper.update(options);
        }
        else {
            createPopper(el, options);
        }
    }
};
var vPopper = {
    bind: bind,
    update: bind,
    unbind: function (el) {
        destroyPopper(el);
    },
};

var vPopperClose = {
    bind: function (el, _a) {
        var value = _a.value, modifiers = _a.modifiers;
        el.omnicaPopperCloserModifiers = modifiers;
        if (typeof value === 'undefined' || value) {
            addCloserListeners(el);
        }
    },
    update: function (el, _a) {
        var value = _a.value, oldValue = _a.oldValue, modifiers = _a.modifiers;
        el.omnicaPopperCloserModifiers = modifiers;
        if (value !== oldValue) {
            if (typeof value === 'undefined' || value) {
                addCloserListeners(el);
            }
            else {
                removeCloserListeners(el);
            }
        }
    },
    unbind: function (el) {
        removeCloserListeners(el);
    },
};

var index = {
    install: function (Vue$1, options) {
        var _a;
        var _vue = Vue$1;
        _vue.__OmnicaPlugins = (_a = _vue.__OmnicaPlugins) !== null && _a !== void 0 ? _a : [];
        if (!_vue.__OmnicaPlugins.includes(this)) {
            _vue.component('UiPopper', __vue_component__);
            _vue.directive('popper', vPopper);
            _vue.directive('popper-close', vPopperClose);
            _vue.mixin({
                beforeCreate: function () {
                    this.$omnicaPopper = Vue.observable(__assign({ container: null }, options));
                },
            });
            _vue.__OmnicaPlugins.push(this);
        }
    },
};

export { __vue_component__ as UiPopper, index as default, vPopper, vPopperClose };
